import { clsx, type ClassValue } from "clsx";
import { format, subDays } from "date-fns";
import { DateRange } from "react-day-picker";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Configuration options for currency formatting
 * @interface FormatCurrencyOptions
 */
export interface FormatCurrencyOptions {
  /** Currency symbol to display (default: "₦") */
  currency?: string;
  /** Whether to show decimal places (default: true) */
  showDecimals?: boolean;
  /** Minimum number of decimal places to show (default: 0) */
  minDecimalPlaces?: number;
  /** Maximum number of decimal places to show (default: 2) */
  maxDecimalPlaces?: number;
  /** Use shorthand notation (K, M, B, T) for large numbers (default: true) */
  useShorthand?: boolean;
  /** Locale string for number formatting (default: "en-NG") */
  locale?: string;
}

/**
 * Formats a numeric value as currency with flexible options
 *
 * @param value - The number or string to format
 * @param options - Formatting options (currency symbol, decimals, shorthand, etc.)
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1500000) // "₦1.50M"
 * formatCurrency(1500000, { useShorthand: false }) // "₦1,500,000"
 * formatCurrency(-2500.50) // "-₦2.50K"
 */
export function formatCurrency(
  value: number | string,
  options: FormatCurrencyOptions = {}
): string {
  const {
    currency = "₦",
    showDecimals = true,
    minDecimalPlaces = 0,
    maxDecimalPlaces = 2,
    useShorthand = true,
    locale = "en-NG",
  } = options;

  // Parse string values, removing any non-numeric characters except decimal and negative
  const numericValue =
    typeof value === "string"
      ? parseFloat(value.replace(/[^0-9.-]+/g, ""))
      : value;

  // Handle invalid numbers
  if (
    isNaN(numericValue) ||
    numericValue === null ||
    numericValue === undefined
  ) {
    return `${currency}0`;
  }

  // Handle negative values
  const isNegative = numericValue < 0;
  const absoluteValue = Math.abs(numericValue);

  // Shorthand formatting (K, M, B, T)
  if (useShorthand) {
    // Trillions (1,000,000,000,000+)
    if (absoluteValue >= 1_000_000_000_000) {
      const formatted = (absoluteValue / 1_000_000_000_000).toFixed(
        maxDecimalPlaces
      );
      return `${isNegative ? "-" : ""}${currency}${formatted}T`;
    }
    // Billions (1,000,000,000+)
    if (absoluteValue >= 1_000_000_000) {
      const formatted = (absoluteValue / 1_000_000_000).toFixed(
        maxDecimalPlaces
      );
      return `${isNegative ? "-" : ""}${currency}${formatted}B`;
    }
    // Millions (1,000,000+)
    if (absoluteValue >= 1_000_000) {
      const formatted = (absoluteValue / 1_000_000).toFixed(maxDecimalPlaces);
      return `${isNegative ? "-" : ""}${currency}${formatted}M`;
    }
    // Thousands (1,000+)
    if (absoluteValue >= 1_000) {
      const formatted = (absoluteValue / 1_000).toFixed(maxDecimalPlaces - 1);
      return `${isNegative ? "-" : ""}${currency}${formatted}K`;
    }
  }

  // Full number formatting with locale support for numbers < 1,000 or when shorthand is disabled
  const formattedNumber = absoluteValue.toLocaleString(locale, {
    minimumFractionDigits: showDecimals ? minDecimalPlaces : 0,
    maximumFractionDigits: showDecimals ? maxDecimalPlaces : 0,
  });

  return `${isNegative ? "-" : ""}${currency}${formattedNumber}`;
}

/**
 * Formats a value as Nigerian Naira with default settings
 * Uses shorthand notation (K, M, B) and shows up to 2 decimal places
 *
 * @example
 * formatNaira(1500000) // "₦1.50M"
 * formatNaira("2500") // "₦2.50K"
 */
export function formatNaira(value: number | string): string {
  return formatCurrency(value);
}

/**
 * Formats a value as Nigerian Naira with full number display (no shorthand)
 * Shows complete number with thousand separators and decimal places
 *
 * @example
 * formatNairaFull(1500000) // "₦1,500,000.00"
 * formatNairaFull(2500.5) // "₦2,500.50"
 */
export function formatNairaFull(value: number | string): string {
  return formatCurrency(value, { useShorthand: false, showDecimals: true });
}

/**
 * Formats a value as Nigerian Naira with shortened notation and minimal decimals
 * Uses 1 decimal place for compact display
 *
 * @example
 * formatNairaShort(1500000) // "₦1.5M"
 * formatNairaShort(2750000) // "₦2.8M"
 */
export function formatNairaShort(value: number | string): string {
  return formatCurrency(value, { useShorthand: true, maxDecimalPlaces: 1 });
}

/**
 * Formats a value as Nigerian Naira without decimal places
 * Rounds to nearest whole number and displays full amount
 *
 * @example
 * formatNairaNoDecimals(1500.99) // "₦1,501"
 * formatNairaNoDecimals(2500000) // "₦2,500,000"
 */
export function formatNairaNoDecimals(value: number | string): string {
  return formatCurrency(value, { showDecimals: false, useShorthand: false });
}

/**
 * Formats a value as US Dollars with American English locale
 *
 * @example
 * formatUSD(1500000) // "$1.50M"
 * formatUSD(2500.50) // "$2,500.50"
 */
export function formatUSD(value: number | string): string {
  return formatCurrency(value, { currency: "$", locale: "en-US" });
}

/**
 * Formats a value as Euros with German locale formatting
 *
 * @example
 * formatEUR(1500000) // "€1.50M"
 * formatEUR(2500.50) // "€2.500,50" (German number format)
 */
export function formatEUR(value: number | string): string {
  return formatCurrency(value, { currency: "€", locale: "de-DE" });
}

/**
 * Formats a value as British Pounds with UK English locale
 *
 * @example
 * formatGBP(1500000) // "£1.50M"
 * formatGBP(2500.50) // "£2,500.50"
 */
export function formatGBP(value: number | string): string {
  return formatCurrency(value, { currency: "£", locale: "en-GB" });
}

/**
 * Capitalizes a name properly, handling single and multiple words
 *
 * @param name - The name to capitalize (can be in any case)
 * @returns The properly capitalized name
 *
 * @example
 * capitalizeName("jOSHUA ISAac") // "Joshua Isaac"
 * capitalizeName("joshua") // "Joshua"
 * capitalizeName("JOSHUA") // "Joshua"
 * capitalizeName("Joshua") // "Joshua"
 * capitalizeName("jOsHua") // "Joshua"
 * capitalizeName("mary jane watson") // "Mary Jane Watson"
 * capitalizeName("") // ""
 * capitalizeName("a") // "A"
 */
export function capitalizeName(name: string): string {
  if (!name || typeof name !== "string") {
    return "";
  }

  return name
    .trim()
    .split(/\s+/) // Split by any whitespace (handles multiple spaces)
    .filter((word) => word.length > 0) // Remove empty strings
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatNGN(amount: string | number) {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

/**
 * Generates a deterministic HSL color from a string.
 * This ensures that a given product name will always have the same color.
 * @param str The input string (e.g., product name).
 * @param s Saturation percentage (default: 60).
 * @param l Lightness percentage (default: 50).
 * @returns An HSL color string (e.g., 'hsl(120, 60%, 50%)').
 */
export function stringToHslColor(str: string, s = 60, l = 50): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

export type DateFilterPreset = "7d" | "30d" | "90d";
export type DateFilterValue = DateFilterPreset | DateRange;

interface ApiParams {
  startDate?: string;
  endDate?: string;
}

/**
 * A utility function to convert the UI's date filter state into API-compatible query parameters.
 * This is the core of the abstraction layer.
 * @param dateFilter The state from the UI ('7d', '30d', '90d', or a DateRange object).
 * @returns An object with startDate and endDate strings.
 */
export const convertDateFilterToParams = (
  dateFilter: DateFilterValue
): ApiParams => {
  if (typeof dateFilter === "string") {
    const endDate = new Date();
    let startDate: Date;
    switch (dateFilter) {
      case "7d":
        startDate = subDays(endDate, 6); // Includes today, so 6 days back
        break;
      case "30d":
        startDate = subDays(endDate, 29);
        break;
      case "90d":
        startDate = subDays(endDate, 89);
        break;
      default:
        startDate = subDays(endDate, 6); // Default to 7 days
    }
    return {
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    };
  }

  if (dateFilter?.from) {
    return {
      startDate: format(dateFilter.from, "yyyy-MM-dd"),
      // If only a start date is selected, use it as the end date as well.
      endDate: format(dateFilter.to || dateFilter.from, "yyyy-MM-dd"),
    };
  }

  return {};
};

/**
 * A utility to format product keys into user-friendly names
 * @param key The product key string
 * @returns The formatted product name
 *
 * @example
 * formatProductName("robogoal") // "RoboGoal"
 * formatProductName("robosave") // "RoboSave"
 */
export const formatProductName = (key: string): string => {
  const nameMap: Record<string, string> = {
    robogoal: "RoboGoal",
    robosave: "RoboSave",
    robosaveBusiness: "RoboSave Business",
    fixedDepositPrime: "Fixed Deposit Prime",
    fixedDepositPro: "Fixed Deposit Pro",
    robocirle: "RoboCircle",
    roboadvance: "RoboAdvance",
    robocredit: "RoboCredit",
    roboflex: "RoboFlex",
    robocollect: "RoboCollect",
    robomass: "RoboMass",
    roboinsight: "RoboInsight",
    walletNubanPayments: "Wallet NUBAN Payments",
    smartPay: "SmartPay",
  };
  return nameMap[key] || key;
};
